/**
 * Created by neo on 14.11.2023
 */
import { makeAutoObservable } from 'mobx';
import { HttpBackend } from '../../../Services/Http/HttpBackend';
import { RouteChallengeLocation, createRouteChallengeLocation } from './RouteChallengeLocation';
import { LocalizedEntity, createLocalizedEntity } from '../../LocalizedEntity';
import { Pageable } from '../../Interfaces/Pageable';
import { Media, createMedia } from '../../Media/Media';
import { MapRegion, createMapRegion } from './MapRegion';
import { RouteChallengeWalkthroughEntry, createRouteChallengeWalkthroughEntry } from './RouteChallengeWalkthroughEntry';
import dayjs from 'dayjs';

export type RouteChallenge = LocalizedEntity & {
  gymId?: string;
  canCreateTeams: boolean;
  maxMembersPerTeam: number;
  minMembersPerTeam: number;
  totalCollectedPoints: number;
  startDateTime: Date;
  endDateTime: Date;
  displayStartDateTime: Date;
  displayEndDateTime: Date;
  locations: RouteChallengeLocation[];
  started: boolean;
  ended: boolean;
  images: Media[];
  mapCenter: MapRegion;
  beforeStartWalkthroughEntries: RouteChallengeWalkthroughEntry[];
  afterEndWalkthroughEntries: RouteChallengeWalkthroughEntry[];
  readonly totalPossiblePoints: number;
  readonly totalDurationInWeeks: number;
  createLocation(atIndex?: number, params?: Partial<RouteChallengeLocation>): RouteChallengeLocation;
  getImage(lang: string): Media | undefined;
};

export function createRouteChallenge(json?: Partial<RouteChallenge>): RouteChallenge {
  const base = createLocalizedEntity(json as any);
  const challenge: RouteChallenge = makeAutoObservable({
    ...base,
    gymId: json?.gymId ?? undefined,
    canCreateTeams: json?.canCreateTeams ?? true,
    maxMembersPerTeam: json?.maxMembersPerTeam ?? 4,
    minMembersPerTeam: json?.minMembersPerTeam ?? 1,
    totalCollectedPoints: json?.totalCollectedPoints ?? 0,
    startDateTime: json?.startDateTime ? new Date(json.startDateTime as any) : new Date(),
    endDateTime: json?.endDateTime ? new Date(json.endDateTime as any) : new Date(),
    displayStartDateTime: json?.displayStartDateTime ? new Date(json.displayStartDateTime as any) : new Date(),
    displayEndDateTime: json?.displayEndDateTime ? new Date(json.displayEndDateTime as any) : new Date(),
    locations: ((json?.locations as any) ?? []).map((js: any) => createRouteChallengeLocation(js)),
    started: json?.started ?? false,
    ended: json?.ended ?? false,
    images: ((json?.images as any) ?? []).map((img: any) => createMedia(img)),
    mapCenter: createMapRegion(json?.mapCenter as any),
    beforeStartWalkthroughEntries: ((json?.beforeStartWalkthroughEntries as any) ?? []).map((entry: any) =>
      createRouteChallengeWalkthroughEntry(entry),
    ),
    afterEndWalkthroughEntries: ((json?.afterEndWalkthroughEntries as any) ?? []).map((entry: any) =>
      createRouteChallengeWalkthroughEntry(entry),
    ),

    get totalPossiblePoints(): number {
      return this.locations.reduce((acc, location) => acc + location.pointsRequired, 0);
    },

    get totalDurationInWeeks(): number {
      return dayjs(this.endDateTime).diff(dayjs(this.startDateTime), 'week');
    },

    createLocation(atIndex?: number, params?: Partial<RouteChallengeLocation>): RouteChallengeLocation {
      const location = createRouteChallengeLocation({
        name: [{ lang: 'en', value: 'New Location' }] as any,
        ...(params ?? {}),
        challengeId: this.id,
      } as any);
      if (atIndex !== undefined && atIndex >= 0 && atIndex <= this.locations.length) {
        this.locations.splice(atIndex, 0, location);
      } else {
        this.locations.push(location);
      }
      return location;
    },

    getImage(lang: string): Media | undefined {
      const languageMedia = this.images.find((a) => a.language?.toLowerCase() === lang.toLowerCase());
      const germanMedia = this.images.find((a) => a.language?.toLowerCase() === 'de');
      return languageMedia ?? germanMedia ?? this.images[0];
    },
  });

  return challenge;
}

export const RouteChallengeApi = {
  find(request?: Partial<Pageable>): Promise<RouteChallenge[]> {
    return HttpBackend.get(`/coach-program/engagement/route-challenges`, request).then((json) =>
      json.map((entry: any) => createRouteChallenge(entry)),
    );
  },

  current(): Promise<RouteChallenge[]> {
    return HttpBackend.get(`/coach-program/engagement/route-challenges/current`).then((json) =>
      json.map((entry: any) => createRouteChallenge(entry)),
    );
  },

  myChallenges(request?: Partial<Pageable>): Promise<RouteChallenge[]> {
    return HttpBackend.get(`/coach-program/engagement/route-challenges/my-challenges`, request).then((json) =>
      (json || []).map((entry: any) => createRouteChallenge(entry)),
    );
  },

  count(): Promise<number> {
    return HttpBackend.get(`/coach-program/engagement/route-challenges/count`).then((res) => res ?? 0);
  },

  findOne(id: string): Promise<RouteChallenge> {
    return HttpBackend.get(`/coach-program/engagement/route-challenges/${id}`).then((json) =>
      createRouteChallenge(json),
    );
  },
};
